import { useEffect } from 'react';
import tw from 'twin.macro';

import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useFieldArray, useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/base';
import { useCreateTemplateMutation, useGetTemplateQuery, useUpdateTemplateMutation } from '@/domains/template';
import { Roles } from '@/libs/graphql';
import { useBackofficeContext } from '@/providers/Backoffice';

import QuestionList from './components/QuestionList';
import { TemplateFormValues, emptyQuestion } from './types';

const TemplateEditor = () => {
  const { templateId } = useParams<{ templateId?: string }>();
  const isCreate = !templateId;
  const navigate = useNavigate();
  const { user } = useBackofficeContext();
  const isViewOnly = user?.roles?.includes(Roles.ViewOnly) ?? false;

  const { data: templateData, loading: templateLoading } = useGetTemplateQuery({
    variables: { templateId: templateId! },
    skip: isCreate,
    fetchPolicy: 'network-only',
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TemplateFormValues>({
    defaultValues: { name: '', questions: [] },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'questions' });

  useEffect(() => {
    if (!isCreate && templateData?.getTemplate) {
      const template = templateData.getTemplate;
      reset({
        name: template.name,
        questions: (template.questions ?? []).map((q) => ({
          title: q.title ?? '',
          duration: q.duration ?? 60,
          tips: (q.tips ?? []).join(', '),
        })),
      });
    }
  }, [templateData, isCreate, reset]);

  const [createTemplate, { loading: createLoading }] = useCreateTemplateMutation({
    onCompleted: (data) => {
      if (data.createTemplate.__typename === 'CreateTemplateResultSuccess') {
        navigate('/backoffice/configuration/templates');
      }
    },
  });

  const [updateTemplate, { loading: updateLoading }] = useUpdateTemplateMutation({
    onCompleted: (data) => {
      if (data.updateTemplate.__typename === 'UpdateTemplateResultSuccess') {
        navigate('/backoffice/configuration/templates');
      }
    },
  });

  const onSubmit = (values: TemplateFormValues) => {
    const questions = values.questions.map((q, index) => ({
      order: index + 1,
      title: q.title,
      duration: Number(q.duration),
      tips: q.tips ? q.tips.split(',').map((s) => s.trim()).filter(Boolean) : [],
    }));

    if (isCreate) {
      createTemplate({
        variables: {
          input: { name: values.name, questions },
        },
      });
    } else {
      updateTemplate({
        variables: {
          input: { templateID: templateId!, name: values.name, questions },
        },
      });
    }
  };

  const isMutating = createLoading || updateLoading;

  if (!isCreate && templateLoading) {
    return (
      <div tw="flex items-center justify-center p-12 text-gray-400 text-sm">
        กำลังโหลด...
      </div>
    );
  }

  return (
    <div tw="flex flex-col gap-6 p-6 max-w-3xl">
      <div tw="flex items-center gap-3">
        <Link
          to="/backoffice/configuration/templates"
          tw="p-2 rounded-lg text-gray-400 hover:(text-gray-600 bg-gray-100) transition-colors duration-100"
        >
          <ArrowLeftIcon tw="w-5 h-5" />
        </Link>
        <div>
          <h4 tw="text-gray-900 font-semibold">
            {isCreate ? 'สร้างชุดคำถาม' : 'แก้ไขชุดคำถาม'}
          </h4>
          <p tw="text-gray-500 text-sm mt-0.5">
            {isCreate ? 'เพิ่มชุดคำถามใหม่สำหรับการสัมภาษณ์' : 'แก้ไขชุดคำถาม'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} tw="flex flex-col gap-6">
        <div tw="rounded-xl border border-gray-200 bg-white p-6 flex flex-col gap-4">
          <h6 tw="font-semibold text-gray-700">ข้อมูลชุดคำถาม</h6>
          <label tw="flex flex-col gap-1">
            <span tw="text-sm font-medium text-gray-700">ชื่อชุดคำถาม *</span>
            <input
              tw="rounded-lg border bg-gray-50 px-3 py-2 text-sm focus:(bg-white outline-none ring-1 ring-primary) disabled:bg-gray-100"
              placeholder="เช่น Software Engineer Interview"
              disabled={isViewOnly}
              {...register('name', { required: 'กรุณากรอกชื่อชุดคำถาม' })}
            />
            {errors.name && (
              <span tw="text-red-500 text-xs">{errors.name.message}</span>
            )}
          </label>
        </div>

        <div tw="rounded-xl border border-gray-200 bg-white p-6">
          <QuestionList
            fields={fields}
            register={register}
            errors={errors}
            isViewOnly={isViewOnly}
            append={append}
            remove={remove}
          />
        </div>

        {!isViewOnly && (
          <div tw="flex justify-end gap-3">
            <Button
              type="button"
              variant="info"
              onClick={() => navigate('/backoffice/configuration/templates')}
            >
              ยกเลิก
            </Button>
            <Button type="submit" disabled={isMutating}>
              {isMutating ? 'กำลังบันทึก...' : isCreate ? 'สร้างชุดคำถาม' : 'บันทึกการเปลี่ยนแปลง'}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default TemplateEditor;
